import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { TFunction } from 'next-i18next';
import EditForm from 'components/editForm/EditForm';
import {
    EnvelopeSizeEnum,
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    Option,
    ValuesStringInterface,
} from 'components/interfaces/GeneralInterface';
import { useTranslation } from 'utils/localization';
import Modal from 'components/modal/Modal';
import {
    AddressApiInterface,
    FieldTypeApiEnum,
    PersonDetailsApiInterface,
    PlayerDetailApiInterface,
} from 'submodules/api_middleware';
import { openPdfContentLinkFromBrowser } from 'services/pdf.service';
import {
    connectStrings,
    replaceDiacritics,
} from 'utils/helpers/browser/string';
import { validatePostalCode } from 'utils/index';
import { MessageType, pushMessage } from 'components/layouts/Toast';
import { getDiscipline } from 'utils/helpers/browser/discipline';
import { getClubDetails, getClubWorkers } from 'services/club.service';
import { getLegalPerson } from 'services/legalPerson.service';
import ButtonLoader from 'components/loading/ButtonLoader';
import {
    ClubInterface,
    ClubMailInterface,
    ClubRepresentativeInterface,
} from 'components/interfaces/ClubInterface';
import { useCountriesList } from 'utils/customHooks/useCountriesList';
import { EditFormOnFieldChangeType } from 'components/interfaces/EditFormInterface';
import { ROW_BREAK } from 'components/editForm/utils/items';
import styles from './GenerateEnvelopeModal.module.scss';

interface Props {
    show: boolean;
    hideCb: () => void;
    person?: PersonDetailsApiInterface;
    player?: PlayerDetailApiInterface;
    club?: ClubInterface;
}

let setOnFieldChange: EditFormOnFieldChangeType;

const GenerateEnvelopeModal = ({
    show,
    hideCb,
    person,
    player,
    club,
}: Props): ReactElement => {
    const { t } = useTranslation();
    const countriesList = useCountriesList();
    const [primaryClubAddress, setPrimaryClubAddress] = useState<{
        address: AddressApiInterface;
        mail: ClubMailInterface;
    }>(null);
    const [clubWorker, setClubWorker] =
        useState<ClubRepresentativeInterface>(null);
    const [hostingClubAddress, setHostingClubAddress] = useState<{
        address: AddressApiInterface;
        mail: ClubMailInterface;
    }>(null);
    const [clubWorkerHosting, setClubWorkerHosting] =
        useState<ClubRepresentativeInterface>(null);

    const [isLoading, setLoading] = useState(!!player);

    useEffect(() => {
        const fetchClubAddresses = async () => {
            let hasLegalPerson = false;
            let hasClubWorker = false;
            if (player.registered_in?.id) {
                const clubResponse = await getClubDetails(
                    player.registered_in.id
                );
                hasLegalPerson = await loadPrimaryClubAddress(
                    clubResponse?.data?.legal_person_id,
                    setPrimaryClubAddress
                );
                hasClubWorker = await loadClubWorkers(
                    clubResponse?.data?.id,
                    setClubWorker
                );
            }
            let hasLegalPersonHosting = false;
            let hasClubWorkerHosting = false;
            if (player.hosting_in?.id) {
                const clubResponse = await getClubDetails(player.hosting_in.id);
                hasLegalPersonHosting = await loadPrimaryClubAddress(
                    clubResponse?.data?.legal_person_id,
                    setHostingClubAddress
                );
                hasClubWorkerHosting = await loadClubWorkers(
                    clubResponse?.data?.id,
                    setClubWorkerHosting
                );
            }

            if (
                !hasClubWorker &&
                !hasLegalPerson &&
                !hasLegalPersonHosting &&
                !hasClubWorkerHosting
            ) {
                setOnFieldChange &&
                    setOnFieldChange(null, false)(
                        'selected_type',
                        AddressTypeEnum.person
                    );
            }
            setLoading(false);
        };
        player && fetchClubAddresses();
    }, [player, setPrimaryClubAddress]);

    const addressTypesOptions: Option[] = [];

    if (person) {
        addressTypesOptions.push({
            value: AddressTypeEnum.person,
            label: t('general.person'),
        });
    }

    if (primaryClubAddress || club) {
        addressTypesOptions.push({
            value: AddressTypeEnum.primary_club,
            label:
                (club ? '' : t('PlayerProfile.registeredInClub') + ' - ') +
                t('address.legalPersonAddress'),
        });
        if (club?.mail?.address || primaryClubAddress?.mail?.address) {
            addressTypesOptions.push({
                value: AddressTypeEnum.club_mail,
                label:
                    (club ? '' : t('PlayerProfile.registeredInClub') + ' - ') +
                    t('ClubProfile.mail'),
            });
        }
    }

    if (clubWorker || club?.representative) {
        addressTypesOptions.push({
            value: AddressTypeEnum.club_representative,
            label:
                (club ? '' : t('PlayerProfile.registeredInClub') + ' - ') +
                t('address.shipping_worker'),
        });
    }

    if (hostingClubAddress) {
        addressTypesOptions.push({
            value: AddressTypeEnum.hosting_club,
            label:
                t('envelope.hostingClub') +
                ' - ' +
                t('address.legalPersonAddress'),
        });
        if (hostingClubAddress?.mail?.address) {
            addressTypesOptions.push({
                value: AddressTypeEnum.club_mail_hosting,
                label:
                    t('envelope.hostingClub') + ' - ' + t('ClubProfile.mail'),
            });
        }
    }

    if (clubWorkerHosting) {
        addressTypesOptions.push({
            value: AddressTypeEnum.club_representative_hosting,
            label:
                t('envelope.hostingClub') +
                ' - ' +
                t('address.shipping_worker'),
        });
    }

    const fields = useMemo<GroupEditFieldInterface[]>(
        () =>
            getFieldsWithLocalisation(
                t,
                addressTypesOptions,
                person,
                countriesList,
                club
                    ? club.billing_fields.address
                    : primaryClubAddress?.address,
                club ? club.representative : clubWorker,
                club ? club.mail : primaryClubAddress?.mail,
                hostingClubAddress?.address,
                clubWorkerHosting,
                hostingClubAddress?.mail
            ),
        [person, club, countriesList, addressTypesOptions]
    );

    const onAction = (values: ValuesStringInterface) => {
        const queryParams: { [key: string]: string } = {
            height:
                getEnvelopeWidth(
                    values[
                        GeneratePersonEnvelopeFormKeys.envelope_type
                    ] as EnvelopeSizeEnum
                ) + 'cm',
            width:
                getEnvelopeHeight(
                    values[
                        GeneratePersonEnvelopeFormKeys.envelope_type
                    ] as EnvelopeSizeEnum
                ) + 'cm',
            marginTop: '0',
            marginRight: '0',
            marginBottom: '0',
            marginLeft: '0',
        };
        Object.keys(GeneratePersonEnvelopeFormKeys).forEach((key) => {
            const value = values[`${key}`];

            if (value) {
                if (
                    key ===
                    GeneratePersonEnvelopeFormKeys.envelope_discipline.toString()
                ) {
                    queryParams[`${key}`] = getDiscipline();
                } else if (
                    key ===
                    GeneratePersonEnvelopeFormKeys.correction_x.toString()
                ) {
                    queryParams.marginLeft = convertMMtoCM(value, 1);
                } else if (
                    key ===
                    GeneratePersonEnvelopeFormKeys.correction_y.toString()
                ) {
                    queryParams.marginTop = convertMMtoCM(value, 1);
                } else {
                    queryParams[key.replace('_2', '').replace('_3', '')] =
                        value;
                }
            }
        });

        openPdfContentLinkFromBrowser(
            '/members_print_envelope',
            replaceDiacritics(
                (club
                    ? club.details.name
                    : connectStrings('_', person.first_name, person.last_name)
                ).toLowerCase()
            ),
            {
                id: club ? club.id + '' : person.id + '',
                ...queryParams,
            }
        );

        pushMessage(
            undefined,
            MessageType.SUCCESS,
            'envelope_is_generating_title'
        );
    };

    return (
        <Modal
            show={show}
            onHide={hideCb}
            showCross
            title={'envelope.title'}
            centerTitle
        >
            {isLoading ? (
                <div className={styles.loader}>
                    <ButtonLoader isDark />
                </div>
            ) : (
                <EditForm
                    fields={fields}
                    action={onAction}
                    withCancelChangesBtn
                    buttonTextKey={'actions.export'}
                    cancelButtonTextKey={'general.cancel'}
                    cancel={hideCb}
                    centerButtons
                    getOnFieldChange={(onFieldChange) =>
                        (setOnFieldChange = onFieldChange)
                    }
                />
            )}
        </Modal>
    );
};

export default GenerateEnvelopeModal;

const getFieldsWithLocalisation = (
    t: TFunction,
    addressTypesOptions: Option[],
    person: PersonDetailsApiInterface,
    countriesList: Option[],
    primaryClubAddress: AddressApiInterface,
    clubRepresentative: ClubRepresentativeInterface,
    mail: ClubMailInterface,
    primaryClubAddressHosting: AddressApiInterface,
    clubRepresentativeHosting: ClubRepresentativeInterface,
    mailHosting: ClubMailInterface
): GroupEditFieldInterface[] => {
    return [
        {
            globalValidation: (state) =>
                !state.selected_type ? t('envelope.select_type') : undefined,
            fields: [
                {
                    name: 'selected_type',
                    label: 'envelope.loadDetailsBy',
                    size: FilterSize.S20,
                    labelSize: LabelSize.TOP,
                    placeholder: 'filters.choose',
                    type: FieldTypeApiEnum.SELECT,
                    values: addressTypesOptions,
                    onChange: (value, state) => {
                        let address: {
                            first_name: string | null;
                            middle_name: string;
                            last_name: string;
                            street: string;
                            street_number: string;
                            city: string;
                            postal_code: string;
                            country_code: string;
                        };
                        if (person) {
                            address = {
                                first_name: person.first_name,
                                middle_name: person.middle_name,
                                last_name: person.last_name,
                                street: person.address?.street,
                                street_number: person.address?.street_number,
                                city: person.address?.city,
                                postal_code: person.address?.postal_code,
                                country_code: person.address?.country_code,
                            };
                        }
                        if (!person || value === AddressTypeEnum.primary_club) {
                            //@ts-ignore-next-line
                            address = {
                                ...primaryClubAddress,
                                first_name: primaryClubAddress.first_line,
                            };
                        }
                        if (value === AddressTypeEnum.club_representative) {
                            //@ts-ignore-next-line
                            address = {
                                first_name: clubRepresentative.first_name,
                                last_name: clubRepresentative.last_name,
                                ...clubRepresentative.address,
                            };
                        }
                        if (value === AddressTypeEnum.club_mail) {
                            //@ts-ignore-next-line

                            address = {
                                first_name: mail.address.first_line,
                                ...mail.address,
                            };
                        }
                        if (value === AddressTypeEnum.hosting_club) {
                            //@ts-ignore-next-line
                            address = {
                                ...primaryClubAddressHosting,
                                first_name:
                                    primaryClubAddressHosting.first_line,
                            };
                        }
                        if (
                            value ===
                            AddressTypeEnum.club_representative_hosting
                        ) {
                            //@ts-ignore-next-line
                            address = {
                                first_name:
                                    clubRepresentativeHosting.first_name,
                                last_name: clubRepresentativeHosting.last_name,
                                ...clubRepresentativeHosting.address,
                            };
                        }
                        if (value === AddressTypeEnum.club_mail_hosting) {
                            //@ts-ignore-next-line

                            address = {
                                first_name: mailHosting.address.first_line,
                                ...mailHosting.address,
                            };
                        }

                        return {
                            ...state,
                            selected_type: value,
                            [GeneratePersonEnvelopeFormKeys.first_name]:
                                address.first_name,
                            [GeneratePersonEnvelopeFormKeys.first_name_2]:
                                address.first_name,
                            [GeneratePersonEnvelopeFormKeys.middle_name]:
                                address.middle_name,
                            [GeneratePersonEnvelopeFormKeys.last_name]:
                                address.last_name,
                            [GeneratePersonEnvelopeFormKeys.last_name_2]:
                                address.last_name,
                            [GeneratePersonEnvelopeFormKeys.last_name_3]:
                                address.last_name,
                            [GeneratePersonEnvelopeFormKeys.street]:
                                address.street,
                            [GeneratePersonEnvelopeFormKeys.street_number]:
                                address.street_number,
                            [GeneratePersonEnvelopeFormKeys.city]: address.city,
                            [GeneratePersonEnvelopeFormKeys.postal_code]:
                                address.postal_code,
                            [GeneratePersonEnvelopeFormKeys.country_code]:
                                address.country_code,
                        };
                    },
                },
            ],
        },
        {
            fields: [
                {
                    name: GeneratePersonEnvelopeFormKeys.first_name,
                    label: 'address.name',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: (state) =>
                        !!state.selected_type &&
                        (state.selected_type === AddressTypeEnum.person ||
                            state.selected_type ===
                                AddressTypeEnum.club_representative_hosting ||
                            state.selected_type ===
                                AddressTypeEnum.club_representative),
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.first_name_2,
                    label: 'general.name',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: (state) =>
                        !!state.selected_type &&
                        !(
                            state.selected_type === AddressTypeEnum.person ||
                            state.selected_type ===
                                AddressTypeEnum.club_representative_hosting ||
                            state.selected_type ===
                                AddressTypeEnum.club_representative
                        ),
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.middle_name,
                    label: 'address.middleName',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) =>
                        !!state.selected_type &&
                        state.selected_type === AddressTypeEnum.person,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.last_name,
                    label: 'address.surname',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    requiredIfSuccess: (state) =>
                        !!state.selected_type &&
                        state.selected_type !== AddressTypeEnum.primary_club,
                    displayIfSuccess: (state) =>
                        !!state.selected_type &&
                        state.selected_type === AddressTypeEnum.person,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.last_name_2,
                    label: 'address.surname',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) =>
                        !!state.selected_type &&
                        state.selected_type !== AddressTypeEnum.person &&
                        (state.selected_type ===
                            AddressTypeEnum.club_representative_hosting ||
                            state.selected_type ===
                                AddressTypeEnum.club_representative),
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.last_name_3,
                    label: 'envelope.secondLine',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) =>
                        !!state.selected_type &&
                        state.selected_type !== AddressTypeEnum.person &&
                        !(
                            state.selected_type ===
                                AddressTypeEnum.club_representative_hosting ||
                            state.selected_type ===
                                AddressTypeEnum.club_representative
                        ),
                },
            ],
        },
        {
            title: 'LegalPersonsPage.address',
            displaySectionIfSuccess: (state) => !!state.selected_type,
            fields: [
                {
                    name: GeneratePersonEnvelopeFormKeys.street,
                    label: 'address.street',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S25,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.street_number,
                    label: 'address.streetNumber',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S15,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                ROW_BREAK,
                {
                    name: GeneratePersonEnvelopeFormKeys.city,
                    label: 'address.city',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S25,
                    labelSize: LabelSize.TOP,
                    required: true,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.postal_code,
                    label: 'address.postalCode',
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S15,
                    labelSize: LabelSize.TOP,
                    required: true,
                    validate: (value, state) =>
                        state.country_code === 'CZ'
                            ? validatePostalCode(value as string, t)
                            : undefined,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.country_code,
                    label: 'address.country',
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    required: true,
                    values: countriesList,
                    placeholder: 'filters.choose',
                    displayIfSuccess: (state) => !!state.selected_type,
                },
            ],
        },
        {
            title: 'envelope.general',
            displaySectionIfSuccess: (state) => !!state.selected_type,
            fields: [
                {
                    name: GeneratePersonEnvelopeFormKeys.envelope_type,
                    value: EnvelopeSizeEnum.long,
                    label: 'envelope.type',
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    required: true,
                    values: Object.keys(EnvelopeSizeEnum).map((key) => ({
                        label: t('envelope.size.' + key),
                        value: key,
                    })),
                    placeholder: 'filters.choose',
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                ROW_BREAK,
                {
                    name: GeneratePersonEnvelopeFormKeys.envelope_discipline,
                    value: false,
                    label: 'envelope.addDiscipline',
                    type: FieldTypeApiEnum.CHECKBOX,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.recorded,
                    value: false,
                    label: 'envelope.recorded',
                    type: FieldTypeApiEnum.CHECKBOX,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
            ],
        },
        {
            title: 'envelope.visualCorrections',
            displaySectionIfSuccess: (state) => !!state.selected_type,
            fields: [
                {
                    name: GeneratePersonEnvelopeFormKeys.correction_x,
                    value: false,
                    label: 'envelope.correctionX',
                    type: FieldTypeApiEnum.NUMBER,
                    min: -10000,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.correction_y,
                    value: false,
                    label: 'envelope.correctionY',
                    type: FieldTypeApiEnum.NUMBER,
                    min: -10000,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.rotate,
                    value: false,
                    label: 'envelope.rotate',
                    type: FieldTypeApiEnum.CHECKBOX,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
                {
                    name: GeneratePersonEnvelopeFormKeys.center,
                    value: false,
                    label: 'envelope.center',
                    type: FieldTypeApiEnum.CHECKBOX,
                    size: FilterSize.S40,
                    labelSize: LabelSize.TOP,
                    displayIfSuccess: (state) => !!state.selected_type,
                },
            ],
        },
    ];
};

const loadPrimaryClubAddress = async (
    legalPersonId: number,
    setPrimaryClubAddress: (primaryClubAddress: {
        address: AddressApiInterface;
        mail: ClubMailInterface;
    }) => void
) => {
    if (!legalPersonId) return false;
    const legalPersonDetailsResponse = await getLegalPerson(legalPersonId);
    const hasLegalPerson = legalPersonDetailsResponse?.data?.address;
    if (hasLegalPerson) {
        setPrimaryClubAddress({
            address: legalPersonDetailsResponse.data.address,
            mail: legalPersonDetailsResponse.data.shipping,
        });
    }
    return hasLegalPerson;
};

const loadClubWorkers = async (
    clubId: number,
    setClubWorker: (clubWorker: ClubRepresentativeInterface) => void
) => {
    if (!clubId) return false;
    const clubWorkersResponse = await getClubWorkers(clubId);
    const hasClubWorker = clubWorkersResponse?.data?.length > 0;
    if (hasClubWorker) {
        const clubWorker = clubWorkersResponse.data[0];
        setClubWorker({
            id: clubWorker.id,
            first_name: clubWorker.first_name,
            last_name: clubWorker.last_name,
            address: { ...clubWorker.address },
        } as ClubRepresentativeInterface);
    }
    return hasClubWorker;
};

export enum GeneratePersonEnvelopeFormKeys {
    first_name = 'first_name',
    first_name_2 = 'first_name_2',
    middle_name = 'middle_name',
    last_name = 'last_name',
    last_name_2 = 'last_name_2',
    last_name_3 = 'last_name_3',
    street = 'street',
    street_number = 'street_number',
    city = 'city',
    postal_code = 'postal_code',
    country_code = 'country_code',
    envelope_type = 'envelope_type',
    envelope_discipline = 'envelope_discipline',
    correction_x = 'correction_x',
    correction_y = 'correction_y',
    rotate = 'rotate',
    recorded = 'recorded',
    center = 'center',
}

export const getEnvelopeHeight = (type: EnvelopeSizeEnum): number =>
    type === EnvelopeSizeEnum.long
        ? 11
        : type === EnvelopeSizeEnum.small
        ? 11.4
        : 16.2;

export const getEnvelopeWidth = (type: EnvelopeSizeEnum): number =>
    type === EnvelopeSizeEnum.long
        ? 22
        : type === EnvelopeSizeEnum.small
        ? 16.2
        : 22.9;

const convertMMtoCM = (mm: string, addCm: number): string => {
    if (!mm) return '';
    //-100 -> -10.0cm
    return parseFloat(mm) / 10 + addCm + 'cm';
};

enum AddressTypeEnum {
    'person' = 'person',
    'primary_club' = 'primary_club',
    'club_representative' = 'club_representative',
    'club_mail' = 'club_mail',
    'hosting_club' = 'hosting_club',
    'club_representative_hosting' = 'club_representative_hosting',
    'club_mail_hosting' = 'club_mail_hosting',
}
